import { CartAddEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events'
import { debounce, formatMoney } from '@theme/utilities'

/**
 * Gets a section DOM element from a HTML string.
 * @param {string} html - The HTML string.
 * @param {string} selector - The selector to use.
 * @returns {HTMLElement | null} The section DOM element.
 */
export function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

/**
 * Formats a number of cents into a money string using a given format.
 * @param {number|string} cents - The cent value to format.
 * @param {string} format - The format string (optional).
 * @returns {string} The formatted money string.
 */
export function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.LTATheme.money.format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', ',')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

/*****************************************
 * [PAGE]: MAIN CART
 * [COMPONENT]: UPSELL PRODUCTS
 ******************************************/

export class LTAMainCartUpsellProducts extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		const mainCartUpsellProducts = document.getElementById('main-cart-upsell-products')

		if (mainCartUpsellProducts) {
			this.innerHTML = mainCartUpsellProducts.outerHTML
			this.querySelector('#main-cart-upsell-products')?.classList.remove('l-hidden-max-tablet')
		}
	}
}

customElements.define('l-cart-upsell-products', LTAMainCartUpsellProducts)

/*****************************************
 * [PAGE]: MAIN CART
 * [COMPONENT]: SHIPPING BAR
 ******************************************/

if (!customElements.get('l-shipping-bar')) {
	class LTAShippingBar extends HTMLElement {
		constructor() {
			super()

			/** @type {{ label?: HTMLElement, progressBar?: HTMLElement, srOnly?: HTMLElement }} */
			this.elements = {}
		}

		connectedCallback() {
			this.cacheElements()
			this.render()

			document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate.bind(this))
		}

		#handleCartUpdate() {
			this.classList.add('is-loading')

			fetch(Theme.routes.cart_url + '.js')
				.then((res) => res.json())
				.then((cartData) => {
					this.dataset.cartTotal = cartData.total_price
				})
				.finally(() => {
					this.render()
					this.classList.remove('is-loading')
				})
		}

		cacheElements() {
			const elements = this.elements

			elements.label = this.querySelector('[data-label]')
			elements.progressBar = this.querySelector('[data-progress-bar]')
			elements.srOnly = this.querySelector('[data-sr-only]')
		}

		_getCartData() {
			const {
				cartTotal = '0',
				thresholdAmount = '0',
				cartCurrency = '',
				shopCurrency = '',
				progressMessage = '',
				successMessage = '',
			} = this.dataset

			return {
				cartTotal: parseFloat(cartTotal) || 0,
				thresholdAmount: parseFloat(thresholdAmount) || 0,
				cartCurrency,
				shopCurrency,
				progressMessage,
				successMessage,
			}
		}

		/**
		 * Calculate the progress and remaining amount for the shipping bar.
		 * Gracefully handle absence of Currency utility.
		 *
		 * @param {number} cartTotal
		 * @param {number} thresholdAmount
		 * @param {string} shopCurrency
		 * @param {string} cartCurrency
		 * @returns {{ progress: number, remaining: number, convertedThreshold: number }}
		 */
		_getProgressData(cartTotal, thresholdAmount, shopCurrency, cartCurrency) {
			let convertedThreshold = thresholdAmount

			// Check if Currency utility is available and has a convert function
			if (typeof window.Currency !== 'undefined' && typeof window.Currency.convert === 'function') {
				convertedThreshold = window.Currency.convert(thresholdAmount, shopCurrency, cartCurrency)
			}

			const difference = Math.abs(convertedThreshold - cartTotal)
			const remaining = Math.max(0, parseFloat((difference / 100).toFixed(2)))
			const progress = convertedThreshold > 0 ? Math.min((cartTotal / convertedThreshold) * 100, 100) : 0

			return { progress, remaining, convertedThreshold }
		}

		render() {
			const { label, progressBar, srOnly } = this.elements
			const { cartTotal, thresholdAmount, shopCurrency, cartCurrency, progressMessage, successMessage } =
				this._getCartData()

			if (
				typeof cartTotal !== 'number' ||
				isNaN(cartTotal) ||
				typeof thresholdAmount !== 'number' ||
				isNaN(thresholdAmount) ||
				!progressBar ||
				!label ||
				!srOnly
			) {
				return
			}

			const { progress, remaining } = this._getProgressData(cartTotal, thresholdAmount, shopCurrency, cartCurrency)

			progressBar.style.setProperty('--l-progress-bar-width', `${progress}%`)

			label.innerHTML =
				progress < 100
					? progressMessage
						? progressMessage.replace('[money]', l_formatMoney((remaining * 100).toString()))
						: ''
					: successMessage

			progressBar.setAttribute('aria-valuenow', progress.toFixed(0))
			srOnly.innerHTML = `${progress.toFixed(0)}%`
		}
	}

	customElements.define('l-shipping-bar', LTAShippingBar)
}

/**********************************
 * [PRODUCT BLOCK]: UPSELL PRODUCT
 ***********************************/

if (!customElements.get('l-upsell-products')) {
	customElements.define(
		'l-upsell-products',
		class LTAProductUpsells extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				const url = this.dataset.url
				if (!url) return
				this.loadRecommendations(url)
			}

			/**
			 * Loads the recommendations from the API.
			 *
			 * @param {string} url - The URL to load the recommendations from.
			 */
			async loadRecommendations(url) {
				const productListEl = this.querySelector('[data-products-list]')
				if (!productListEl) return

				try {
					const response = await fetch(url)
					const text = await response.text()
					const wrapper = document.createElement('div')
					wrapper.innerHTML = text
					const recommendations = wrapper.querySelector('.shopify-section')

					if (!recommendations) return

					// Update atc buttons label if available
					const atcBtnLabel = this.dataset.atcBtnLabel
					if (atcBtnLabel) {
						recommendations.querySelectorAll('[data-atc]').forEach((atcBtn) => {
							atcBtn.textContent = atcBtnLabel
						})
					}

					// Insert recommendations to product list
					productListEl.innerHTML = recommendations.innerHTML
					this.classList.remove('is-loading')
				} catch (error) {
					this.remove()
				}
			}
		}
	)
}

/*****************************************
 * [PAGE]: MAIN PRODUCT
 * [COMPONENT]: STICKY ADD TO CART
 ******************************************/

export class LTAProductStickyCart extends HTMLElement {
	constructor() {
		super()

		this.variantUpdated = this.#handleOnVariantChanged.bind(this)
		this.cartAdded = this.#handleCartAdd.bind(this)
		this.debouncedCheckStickyATC = debounce(this.handleOnScroll.bind(this), 50)
	}

	connectedCallback() {
		this.scrollToVariantPicker()

		document.addEventListener(CartAddEvent.eventName, this.cartAdded)
		document.addEventListener(ThemeEvents.variantUpdate, this.variantUpdated)
		document.addEventListener('scroll', this.debouncedCheckStickyATC)
	}

	disconnectedCallback() {
		document.removeEventListener(CartAddEvent.eventName, this.cartAdded)
		document.removeEventListener(ThemeEvents.variantUpdate, this.variantUpdated)
		document.removeEventListener('scroll', this.debouncedCheckStickyATC)
	}

	handleOnScroll() {
		const mainSubmitEl = document.getElementById(`ProductInformation-${this.dataset.sectionId}`)
		const productFormComponentEl = mainSubmitEl?.querySelector('product-form-component')
		if (!productFormComponentEl) return

		console.log(productFormComponentEl)

		const rect = productFormComponentEl.getBoundingClientRect()
		const scrollPosition = window.scrollY || document.documentElement.scrollTop

		// Calculate the main submit button's position from the top of the page
		const mainSubmitOffsetTop = scrollPosition + rect.top

		// Check if we're in the space between the top and the main submit button
		const isAtTop = scrollPosition < mainSubmitOffsetTop

		// Check if productFormComponentEl is visible in the viewport
		const isMainSubmitInViewport = rect.top < window.innerHeight && rect.bottom > 0

		// Show sticky ATC only when not at top AND main submit is out of view
		const isVisible = !isAtTop && !isMainSubmitInViewport

		this.classList.toggle('is-active', isVisible)
	}

	scrollToVariantPicker() {
		const scrollToVariantPickerButtonEl = this.querySelector('[data-scroll-to-variant-picker]')
		if (!scrollToVariantPickerButtonEl) return

		scrollToVariantPickerButtonEl.addEventListener('click', () => {
			const variantPickerEl = document.querySelector('variant-picker')

			if (!variantPickerEl) return

			variantPickerEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
			this.classList.remove('is-active')
		})
	}

	/**
	 * Handles a variant update event by replacing the current media gallery with a new one.
	 *
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	#handleOnVariantChanged(event) {
		const source = event.detail.data.html
		const variant = event.detail.resource

		// Check selected variant is unavailable or not
		this.classList.toggle('l-hidden', !variant)

		const newHTML = l_getSectionDOM(source.body.innerHTML, '#product-sticky-cart')

		if (newHTML) {
			this.innerHTML = newHTML.outerHTML
		}
	}

	/**
	 * Handles a variant update event by replacing the current media gallery with a new one.
	 *
	 * @param {CartAddEvent} event - The variant update event.
	 */
	#handleCartAdd(event) {
		this.querySelector('[data-submit-button]')?.classList?.add('atc-added')

		debounce(() => {
			this.querySelector('button')?.classList.remove('atc-added')
		}, 2000)()
	}
}

customElements.define('l-product-sticky-atc', LTAProductStickyCart)

